/**
 * Tab Navigation Hook
 * 
 * Implements W3C ARIA Authoring Practices Guide (APG) keyboard navigation for tabs.
 * 
 * **Features:**
 * - Arrow key navigation (Left/Right or Up/Down)
 * - Home/End key support
 * - Automatic focus management
 * - Optional automatic activation (focus = select)
 * - Accessible ARIA attributes
 * 
 * **W3C APG Reference:**
 * https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
 * 
 * @module hooks/useTabNavigation
 */

import { useRef, useCallback, type KeyboardEvent } from 'react';

/**
 * Options for tab navigation
 */
export interface UseTabNavigationOptions<T extends string> {
  /**
   * Current active tab value
   */
  activeTab: T;
  
  /**
   * Callback when tab changes
   */
  onTabChange: (tab: T) => void;
  
  /**
   * Array of tab values in order
   */
  tabs: readonly T[];
  
  /**
   * Orientation of tabs (affects which arrow keys work)
   * @default 'horizontal'
   */
  orientation?: 'horizontal' | 'vertical';
  
  /**
   * Whether to automatically activate tabs on focus (vs. requiring click/Enter)
   * @default false (manual activation - focus != select, requires Enter/Space)
   */
  automaticActivation?: boolean;
}

/**
 * Return type for useTabNavigation hook
 */
export interface UseTabNavigationReturn<T extends string> {
  /**
   * Props for the tab list container
   */
  tabListProps: {
    role: 'tablist';
    'aria-orientation': 'horizontal' | 'vertical';
  };
  
  /**
   * Get props for a tab button
   * 
   * @param tab - The tab value
   */
  getTabProps: (tab: T) => {
    role: 'tab';
    'aria-selected': boolean;
    'aria-controls': string;
    id: string;
    tabIndex: 0 | -1;
    onKeyDown: (e: KeyboardEvent) => void;
    onClick: () => void;
    ref: (el: HTMLElement | null) => void;
  };
  
  /**
   * Get props for a tab panel
   * 
   * @param tab - The tab value
   */
  getTabPanelProps: (tab: T) => {
    role: 'tabpanel';
    'aria-labelledby': string;
    id: string;
    tabIndex: 0;
    hidden?: boolean;
  };
}

/**
 * Tab Navigation Hook
 * 
 * Provides W3C APG compliant keyboard navigation for tabs.
 * 
 * @example
 * ```tsx
 * type TabValue = 'memberships' | 'passes';
 * 
 * function SubscriptionTabs() {
 *   const [activeTab, setActiveTab] = useState<TabValue>('memberships');
 *   
 *   const { tabListProps, getTabProps, getTabPanelProps } = useTabNavigation({
 *     activeTab,
 *     onTabChange: setActiveTab,
 *     tabs: ['memberships', 'passes'],
 *     orientation: 'horizontal',
 *   });
 *   
 *   return (
 *     <div>
 *       <div {...tabListProps}>
 *         <button {...getTabProps('memberships')}>Memberships</button>
 *         <button {...getTabProps('passes')}>Passes</button>
 *       </div>
 *       <div {...getTabPanelProps('memberships')}>
 *         Memberships content
 *       </div>
 *       <div {...getTabPanelProps('passes')}>
 *         Passes content
 *       </div>
 *     </div>
 *   );
 * }
 * ```
 */
export function useTabNavigation<T extends string>({
  activeTab,
  onTabChange,
  tabs,
  orientation = 'horizontal',
  automaticActivation = false,
}: UseTabNavigationOptions<T>): UseTabNavigationReturn<T> {
  // Store refs to tab elements for focus management
  const tabRefs = useRef<Map<T, HTMLElement>>(new Map());
  
  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback((e: KeyboardEvent, currentTab: T) => {
    const currentIndex = tabs.indexOf(currentTab);
    let nextIndex = currentIndex;
    
    // Determine which keys to use based on orientation
    const prevKey = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';
    const nextKey = orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown';
    
    switch (e.key) {
      case prevKey:
        e.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        break;
        
      case nextKey:
        e.preventDefault();
        nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        break;
        
      case 'Home':
        e.preventDefault();
        nextIndex = 0;
        break;
        
      case 'End':
        e.preventDefault();
        nextIndex = tabs.length - 1;
        break;
        
      default:
        return; // Don't handle other keys
    }
    
    const nextTab = tabs[nextIndex];
    
    // Move focus to next tab
    const nextTabElement = tabRefs.current.get(nextTab);
    if (nextTabElement) {
      nextTabElement.focus();
      
      // With automatic activation, change tab on focus
      if (automaticActivation) {
        onTabChange(nextTab);
      }
    }
  }, [tabs, orientation, automaticActivation, onTabChange]);
  
  /**
   * Handle tab click
   */
  const handleTabClick = useCallback((tab: T) => {
    onTabChange(tab);
  }, [onTabChange]);
  
  /**
   * Store ref to tab element
   */
  const setTabRef = useCallback((tab: T) => (el: HTMLElement | null) => {
    if (el) {
      tabRefs.current.set(tab, el);
    } else {
      tabRefs.current.delete(tab);
    }
  }, []);
  
  /**
   * Tab list container props
   */
  const tabListProps = {
    role: 'tablist' as const,
    'aria-orientation': orientation,
  };
  
  /**
   * Get props for a tab button
   */
  const getTabProps = useCallback((tab: T) => {
    const isSelected = tab === activeTab;
    
    return {
      role: 'tab' as const,
      'aria-selected': isSelected,
      'aria-controls': `tabpanel-${tab}`,
      id: `tab-${tab}`,
      // Only selected tab is in tab order
      tabIndex: (isSelected ? 0 : -1) as 0 | -1,
      onKeyDown: (e: KeyboardEvent) => handleKeyDown(e, tab),
      onClick: () => handleTabClick(tab),
      ref: setTabRef(tab),
    };
  }, [activeTab, handleKeyDown, handleTabClick, setTabRef]);
  
  /**
   * Get props for a tab panel
   */
  const getTabPanelProps = useCallback((tab: T) => {
    const isSelected = tab === activeTab;
    
    return {
      role: 'tabpanel' as const,
      'aria-labelledby': `tab-${tab}`,
      id: `tabpanel-${tab}`,
      tabIndex: 0 as const,
      // Hide non-selected panels (not `display: none` - that breaks accessibility)
      ...(isSelected ? {} : { hidden: true }),
    };
  }, [activeTab]);
  
  return {
    tabListProps,
    getTabProps,
    getTabPanelProps,
  };
}
